import { DeductionListJson, type DeductionKind } from "@/api/deduction.tsx"
import { api_fetch, api_fetch_throws, HttpResponse, HttpError } from "@/api/client.tsx"
import { Err, Ok, Result } from "@/utils"

export interface Split {
  id: string
  account_id?: string
  deduction_id: string

  amount: number

  customer_name?: string
  retailer_name?: string

  product_id?: string
  product_line_id?: string
  product_category_id?: string
  product_line?: string
  code_description?: string
  promo_type?: string
  promo_sub_type?: string

  product_category?: string
  /** The brand's accounting product group — 1st Phorm's NetSuite "Related Product Group". */
  related_product_group?: string
  /** The brand's chargeback type — 1st Phorm's NetSuite "Chargeback Type". */
  chargeback_type?: string
  execution_date?: string
  retailer_invoice_number?: string

  name: string
  expense_account?: string
  account_number?: string
  /** Accounting-system internal ID for the linked reason code / credit-memo item. */
  account_remote_id?: string
  promotion_id?: string
  promotion_remote_id?: string
  promotion_name?: string
  promotion_type?: string
  promotion_discount_percentage?: number
  promotion_discount_amount?: number
  /** Matched promotion's start/end dates (for accrual reporting; opt-in org-field columns). */
  promotion_start_date?: string | null
  promotion_end_date?: string | null
  /** Trade spend classification from the linked reason code (opt-in org field / export). */
  is_trade?: boolean
  /** Accounting item (product/service) from the linked reason code — what the split's credit memo line posts through. */
  product_service?: string
}

export type TransactionKind = "repayment" | "invoice" | "deduction" | "prepayment"
export const CHECK_STATUS = ["new", "split", "coded"] as const
export type CheckStatus = (typeof CHECK_STATUS)[number]

// export interface Check {
//   id: string
//   org_id: string
//   number: string
//   date: string
//   amount: number
//   status: CheckStatus
//   source: string
// }

export interface TransactionJson extends Omit<DeductionListJson, "kind" | "task"> {
  id: string
  description?: string
  kind: TransactionKind
  accounting_status: CheckStatus
  coded: boolean
  can_auto_code: boolean
  splits: Split[]
  // Comma-separated remote promo IDs when enabled for org
  promotion_remote_ids?: string
  /**
   * The promotion this transaction is matched to. A GL-coding split wins over the deduction-level
   * `deduction_promotion_match` — it is coded line by line, so it is the more specific answer.
   */
  promotion_id?: string
  promotion_name?: string
  promotion_remote_id?: string
  /** Which link named the promotion above; `split` means the GL coding governs it. */
  promotion_match_source?: "split" | "deduction"
  /** Date the payment posts (clearing/close date in PromoMash terms). */
  posting_date?: string | null
  /** Timestamp when the check was successfully applied in the connected accounting system. */
  pushed_at?: string | null
  /** Date the deduction first reached the validated status. */
  validated_date?: string | null
  /**
   * Backup has arrived since a line was GL-coded: the line was coded while the deduction had no
   * backup (often to a holding account like "Other Deductions") and it has backup now.
   */
  backup_since_coding?: boolean
}

/**
 * Bounding box for a line item on the check PDF
 * Coordinates are in inches (PDF coordinate system)
 */
export interface LineBoundingBox {
  /** 1-based page number */
  page: number
  /** Left x coordinate in inches */
  x: number
  /** Top y coordinate in inches */
  y: number
  /** Width in inches */
  width: number
  /** Height in inches */
  height: number
}

/**
 * Page dimensions for the check PDF, keyed by 1-based page number
 * Values are [width, height] in inches
 */
export type PageDimensions = Record<number, [number, number]>

export interface CheckDetail {
  id?: string
  number: string
  amount?: number
  date?: string
  s3_uri?: string
  transactions: TransactionJson[]
  source: string
  customer_name?: string
  count_hint?: number
  status: CheckStatus
  transaction_count?: number
  /** Bounding boxes for check line items, keyed by invoice_number */
  line_bounding_boxes?: Record<string, LineBoundingBox>
  /** Page dimensions for the check PDF */
  page_dimensions?: PageDimensions
  /** ID of the credit memo for this check, if one has been created */
  credit_memo_id?: string
  /** QBO Payment Id once applied (cash applied in QuickBooks). Present = applied. */
  applied_payment_remote_id?: string
  /** QBO CreditMemo Id when applied was detected via a synced credit memo. Present = applied. */
  applied_credit_memo_remote_id?: string
  applied_at?: string
  applied_source?: string
  /** Latest posting date across transactions on this check (when org field enabled). */
  posting_date?: string | null
  /** Net of all transaction lines (signed sum). */
  computed_check_total?: number
  invoices_total?: number
  repayments_total?: number
  /** Positive magnitude of deduction lines. */
  deductions_total?: number
  deduction_rate_pct?: number | null
}

export interface ParsedCheckRow {
  invoice_date: string
  invoice_number: string
  description?: string
  gross_amount?: number
  discount_amount?: number
  invoice_amount: number
}

export interface ParsedCheckMeta {
  check_number?: string
  check_date?: string
  check_amount?: number
  source: string
}

export interface ParseCheckResponse {
  rows: ParsedCheckRow[]
  meta: ParsedCheckMeta
  file_id: string
  s3_uri: string
}

/// High-level check info returned by the global search endpoint.
export interface CheckSearchResult {
  number: string
  date?: string
  amount?: number
  customer_name: string
  deduction_count: number
}

export type RemoteDocKind = "credit_memo" | "payment" | "invoice" | "journal_entry"

/** A document in the accounting system, and the check or deduction it belongs to here, if any. */
export interface RemoteReference {
  kind: RemoteDocKind
  remote_id: string
  doc_number: string | null
  customer_name: string | null
  date: string | null
  amount: number | null
  check_number: string | null
  deduction_id: string | null
}

export async function lookupRemoteReferences(q: string): Promise<RemoteReference[]> {
  const response = await api_fetch<RemoteReference[]>("/accounting/remote-reference", { params: { q } })
  if (response.ok) {
    return response.value.data
  } else {
    throw new Error(`Failed to look up document: ${response.error.message}`)
  }
}

export async function searchChecks(q: string): Promise<CheckSearchResult[]> {
  const response = await api_fetch<CheckSearchResult[]>("/accounting/checks/search", { params: { q } })
  if (response.ok) {
    return response.value.data
  } else {
    throw new Error(`Failed to search checks: ${response.error.message}`)
  }
}

export interface BulkPromotionMatchResponse {
  updated_count: number
  skipped_count: number
}

export async function bulkCreatePromotionMatch(
  transactionIds: string[],
  promotionId: string
): Promise<Result<HttpResponse<BulkPromotionMatchResponse>, HttpError>> {
  return await api_fetch<BulkPromotionMatchResponse>("/accounting/promotion-matches/bulk", {
    method: "POST",
    body: {
      deduction_ids: transactionIds,
      promotion_id: promotionId,
    },
  })
}

export interface UpdateCheckPayload {
  number?: string
  date?: string
  amount?: number
  /** Explicit approval to rename the check to a number already used by another check */
  allow_existing_number?: boolean
  /** Manually mark applied / not applied. Un-applying a check applied in the accounting system is refused. */
  applied?: boolean
}

/** Server message returned when a check-number change collides with an existing check. */
export const CHECK_NUMBER_EXISTS_MESSAGE = "A check with this number already exists"

/** True when an updateCheck error means the new number is already in use (user can approve override). */
export function is_check_number_conflict(error: unknown): boolean {
  return error instanceof Error && error.message.includes(CHECK_NUMBER_EXISTS_MESSAGE)
}

export async function updateCheck(checkId: string | undefined, payload: UpdateCheckPayload): Promise<CheckDetail> {
  // A check with no saved row has no id, and interpolating `undefined` posted to `/check/undefined`,
  // which the server rejects as an unparseable id -- the edit looked like it simply failed.
  if (!checkId) {
    throw new Error("This check has no saved record yet. Reload the page and try again.")
  }
  const res = await api_fetch<{ check: CheckDetail }>(`/check/${checkId}`, {
    method: "POST",
    body: payload,
  })
  if (!res.ok) {
    throw res.error
  }
  return res.value.data.check
}

// Accounting Invoice types and functions

export interface AccountingInvoiceJson {
  id: string
  invoice_number: string
  invoice_date?: string
  total_amount: number
  /**
   * Remaining balance in the accounting system, when synced. `undefined`/`null` = never synced (not a
   * blocker). A value below `total_amount` means the invoice is already partly/fully paid and can't
   * take the full payment — surfaced in the picker and blocked before applying.
   */
  amount_due?: number | null
  /** Invoice status in the accounting system (e.g. "AUTHORISED", "PAID"), when synced. */
  status?: string | null
  /** The deduction ID this invoice is assigned to, if any */
  assigned_id?: string
  /**
   * The check that assigned transaction sits on. Lets the Apply Cash page tell an invoice paid
   * across several checks (UNFI's SI00003941 / SI00003941A) from one claimed by another line of
   * the same check — only the latter is reserved.
   */
  assigned_check_number?: string
  /**
   * What that assigned transaction settles of the invoice (cash plus early-pay discount), for a
   * `kind = Invoice` payment. Summed over other checks' links it is the invoice's "paid elsewhere"
   * amount, which makes this check's remainder a partial payment rather than a short pay.
   */
  assigned_settle_amount?: number | string | null
  /**
   * The reference printed on the invoice (Xero's `Reference`), which brands fill with the customer's
   * PO. Matched against a line's `po_number` when the remitted invoice number names nothing.
   */
  reference?: string | null
}

/**
 * `checkNumber` widens a customer-scoped list with the check's own invoices billed to other
 * accounting customers — one remittance often pays several (Albertsons → Shaw's + Albertsons DCs).
 */
export async function listAccountingInvoices(
  customerRemoteId?: string,
  checkNumber?: string
): Promise<Result<HttpResponse<AccountingInvoiceJson[]>, HttpError>> {
  const params: Record<string, string> = {}
  if (customerRemoteId) {
    params.customer_remote_id = customerRemoteId
  }
  if (checkNumber) {
    params.check_number = checkNumber
  }
  return await api_fetch<AccountingInvoiceJson[]>("/accounting/invoices", { params })
}

export async function linkTransactionInvoice(
  deductionId: string,
  accountingInvoiceId: string
): Promise<Result<HttpResponse<{ success: boolean }>, HttpError>> {
  return await api_fetch<{ success: boolean }>("/accounting/transaction-invoice", {
    method: "POST",
    body: {
      deduction_id: deductionId,
      accounting_invoice_id: accountingInvoiceId,
    },
  })
}

export async function unlinkTransactionInvoice(
  deductionId: string
): Promise<Result<HttpResponse<{ success: boolean }>, HttpError>> {
  return await api_fetch<{ success: boolean }>(`/accounting/transaction-invoice/${deductionId}`, {
    method: "DELETE",
  })
}

// Accounting Check types and functions (for cash application)

export interface AccountingCheckJson {
  id: string
  check_number: string
  check_date?: string
  total_amount: number
  unapplied_amount?: number
  customer_name?: string
  payment_method?: string
  /** The internal check ID this accounting check is linked to, if any */
  linked_check_id?: string
}

export async function listAccountingChecks(): Promise<Result<HttpResponse<AccountingCheckJson[]>, HttpError>> {
  return await api_fetch<AccountingCheckJson[]>("/accounting/checks-from-accounting")
}

export async function linkTransactionCheck(
  checkId: string,
  accountingCheckId: string
): Promise<Result<HttpResponse<{ success: boolean }>, HttpError>> {
  return await api_fetch<{ success: boolean }>("/accounting/transaction-check", {
    method: "POST",
    body: {
      check_id: checkId,
      accounting_check_id: accountingCheckId,
    },
  })
}

export async function unlinkTransactionCheck(
  checkId: string
): Promise<Result<HttpResponse<{ success: boolean }>, HttpError>> {
  return await api_fetch<{ success: boolean }>(`/accounting/transaction-check/${checkId}`, {
    method: "DELETE",
  })
}

// Synced accounting data inspector (settings)

export interface AccountingSyncedListResponse<T> {
  items: T[]
  total: number
  limit: number
  offset: number
}

export interface AccountingSyncedInvoiceSummary {
  id: string
  remote_id: string
  invoice_number: string
  invoice_date?: string
  total_amount: number
  customer_name?: string
  status?: string
  updated_at: string
}

export interface AccountingSyncedCheckSummary {
  id: string
  remote_id: string
  check_number: string
  check_date?: string
  total_amount: number
  customer_name?: string
  payment_method?: string
  status?: string
  updated_at: string
}

export interface AccountingSyncedInvoiceDetail {
  id: string
  remote_id: string
  customer_id?: string
  customer_name?: string
  invoice_number: string
  invoice_date?: string
  due_date?: string
  total_amount: number
  amount_due?: number
  status?: string
  description?: string
  data: unknown
  created_at: string
  updated_at: string
}

export interface AccountingSyncedCheckDetail {
  id: string
  remote_id: string
  customer_id?: string
  customer_name?: string
  check_number: string
  check_date?: string
  total_amount: number
  unapplied_amount?: number
  payment_method?: string
  status?: string
  memo?: string
  data: unknown
  created_at: string
  updated_at: string
}

export interface AccountingSyncedCustomerSummary {
  id: string
  remote_id: string
  display_name: string
  /** `null` when unknown — customers derived from synced transactions never carry a status. */
  active?: boolean | null
  updated_at: string
}

export interface AccountingSyncedCreditMemoSummary {
  id: string
  remote_id: string
  doc_number?: string
  txn_date?: string
  total_amount: number
  customer_name?: string
  updated_at: string
}

export interface AccountingSyncedCustomerDetail {
  id: string
  remote_id: string
  display_name: string
  /** `null` when unknown — see [AccountingSyncedCustomerSummary]. */
  active?: boolean | null
  data: unknown
  created_at: string
  updated_at: string
}

export interface AccountingSyncedCreditMemoDetail {
  id: string
  remote_id: string
  doc_number?: string
  customer_id?: string
  customer_name?: string
  txn_date?: string
  total_amount: number
  memo?: string
  data: unknown
  created_at: string
  updated_at: string
}

export interface ListSyncedAccountingParams {
  limit?: number
  offset?: number
  q?: string
  sort_by?: "updated" | "date"
  sort_dir?: "asc" | "desc"
}

function buildSyncedAccountingQuery(params: ListSyncedAccountingParams): Record<string, string | number> {
  const query: Record<string, string | number> = {
    limit: params.limit ?? 50,
    offset: params.offset ?? 0,
  }
  if (params.q) {
    query.q = params.q
  }
  if (params.sort_by) {
    query.sort_by = params.sort_by
  }
  if (params.sort_dir) {
    query.sort_dir = params.sort_dir
  }
  return query
}

export async function listSyncedAccountingInvoices(
  params: ListSyncedAccountingParams = {}
): Promise<Result<HttpResponse<AccountingSyncedListResponse<AccountingSyncedInvoiceSummary>>, HttpError>> {
  const query = buildSyncedAccountingQuery(params)
  return api_fetch<AccountingSyncedListResponse<AccountingSyncedInvoiceSummary>>("/accounting/synced/invoices", {
    params: query,
  })
}

export async function listSyncedAccountingChecks(
  params: ListSyncedAccountingParams = {}
): Promise<Result<HttpResponse<AccountingSyncedListResponse<AccountingSyncedCheckSummary>>, HttpError>> {
  const query = buildSyncedAccountingQuery(params)
  return api_fetch<AccountingSyncedListResponse<AccountingSyncedCheckSummary>>("/accounting/synced/checks", {
    params: query,
  })
}

export async function getSyncedAccountingInvoice(
  id: string
): Promise<Result<HttpResponse<AccountingSyncedInvoiceDetail>, HttpError>> {
  return api_fetch<AccountingSyncedInvoiceDetail>(`/accounting/synced/invoices/${id}`)
}

/** URL of the invoice PDF rendered live by the connected accounting system (Xero / QuickBooks). */
export function accountingInvoicePdfUrl(accountingInvoiceId: string): string {
  return `/api/accounting/invoice/${accountingInvoiceId}/pdf`
}

export async function getSyncedAccountingCheck(
  id: string
): Promise<Result<HttpResponse<AccountingSyncedCheckDetail>, HttpError>> {
  return api_fetch<AccountingSyncedCheckDetail>(`/accounting/synced/checks/${id}`)
}

export async function listSyncedAccountingCustomers(
  params: ListSyncedAccountingParams = {}
): Promise<Result<HttpResponse<AccountingSyncedListResponse<AccountingSyncedCustomerSummary>>, HttpError>> {
  const query = buildSyncedAccountingQuery(params)
  return api_fetch<AccountingSyncedListResponse<AccountingSyncedCustomerSummary>>("/accounting/synced/customers", {
    params: query,
  })
}

export async function listSyncedAccountingCreditMemos(
  params: ListSyncedAccountingParams = {}
): Promise<Result<HttpResponse<AccountingSyncedListResponse<AccountingSyncedCreditMemoSummary>>, HttpError>> {
  const query = buildSyncedAccountingQuery(params)
  return api_fetch<AccountingSyncedListResponse<AccountingSyncedCreditMemoSummary>>("/accounting/synced/credit-memos", {
    params: query,
  })
}

export async function getSyncedAccountingCustomer(
  id: string
): Promise<Result<HttpResponse<AccountingSyncedCustomerDetail>, HttpError>> {
  return api_fetch<AccountingSyncedCustomerDetail>(`/accounting/synced/customers/${id}`)
}

export async function getSyncedAccountingCreditMemo(
  id: string
): Promise<Result<HttpResponse<AccountingSyncedCreditMemoDetail>, HttpError>> {
  return api_fetch<AccountingSyncedCreditMemoDetail>(`/accounting/synced/credit-memos/${id}`)
}

// Cash Application types and functions

/** KeHE's BI allowance as configured for the org: the account it is written off to and its rate. */
export interface BiAllowancePreview {
  gl_account_id: string
  gl_account_name: string
  product_service?: string | null
  /** Percent of each invoice's total (2 = 2%). */
  percentage: number
  /**
   * Memo prefix of the lines and the name the page uses: "KeHE BI allowance", or another payer's
   * contracted off-invoice allowance booked the same way ("Walmart allowance"). Absent on a
   * response from a server that predates it.
   */
  label?: string
}

/**
 * A deduction on the check that the accounting system has already applied to one of the check's
 * invoices as a credit note (the brand's own CN-00000117 on SI-00004219, listed again on Thrive's
 * advice). Left off the credit memo and the tables; its invoice is applied for its total less this.
 */
export interface AlreadyCreditedLine {
  /** `null` for a credit note the remittance nets off without listing it (no deduction behind it). */
  deduction_id: string | null
  /** The deduction's number as remitted; `null` when the remittance does not list the credit. */
  invoice_number: string | null
  kind: TransactionKind | null
  /** What the accounting system applied, always positive — equal to the deduction's amount. */
  amount: number
  accounting_invoice_id: string
  accounting_invoice_number: string
  /** The credit note's number as the accounting system prints it. */
  credit_note_number: string
  credit_note_remote_id?: string
}

/**
 * Deductions that only true a customer's figure for one of our invoices down to the accounting
 * system's total — Walmart's reason 100 write-off, UNFI's `PRC PRICING` on an invoice priced below
 * the order. Folded into that invoice's payment line (cash, gross and discount) and left off the
 * credit memo, so nothing here needs coding.
 */
export interface PriceTrueUp {
  invoice_line_id: string
  accounting_invoice_id: string
  accounting_invoice_number: string
  deduction_ids: string[]
  /** Their numbers as remitted. */
  deduction_numbers: string[]
  /** Their cash, always positive. */
  true_up_amount: number
  /** The invoice's cash once the true-up is taken off it. */
  cash: number
  /** The early-pay discount that leaves: the invoice total less that cash. */
  discount: number
}

/**
 * An invoice the accounting system already shows as paid in full that the customer paid and took
 * straight back on this check (H-E-B's `INV33626` and `INV33626-DG`). Both lines leave the check:
 * together they move no money.
 */
export interface ClosedInvoiceWash {
  invoice_line_id: string
  /** The invoice line's number as remitted. */
  invoice_number: string
  deduction_ids: string[]
  /** The reversals' numbers as remitted. */
  deduction_numbers: string[]
  /** The cash paid and taken back, always positive. */
  amount: number
  accounting_invoice_id: string
  accounting_invoice_number: string
}

export interface InvoiceAssignmentLine {
  deduction_id: string
  invoice_number: string
  /** The customer's PO for this line, as remitted. */
  po_number?: string | null
  deduction_amount: number
  kind: TransactionKind
  accounting_invoice_id?: string
  accounting_invoice_number?: string
  /**
   * Invoice-kind lines only: the dollar amount the check + credit memo settle for this invoice
   * (paid amount plus the early-pay discount). The gap between the assigned accounting invoice's
   * total and this is the invoice's short pay.
   */
  settle_amount?: number
  /**
   * Invoice-kind lines only: the early-pay discount taken on the payment itself ("Disc. Taken").
   * Present only when the org's discounts ride on the payment rather than the credit memo — it is
   * then a third funding source alongside cash and the credit memo when balancing.
   */
  early_pay_discount?: number
  /** Multi-customer checks only: the `CustomerGroupPreview.key` this row belongs to. */
  customer_group_key?: string
}

/**
 * A repayment that pays back a deduction already rebilled (Open Deductions → Rebill): the server
 * rewrites it into the payment of the rebill invoice, so the invoice table lists it under that
 * invoice's number rather than the repayment's.
 */
export interface RebillSettlement {
  deduction_id: string
  /** The repayment as remitted (`INV33661*`). */
  repayment_number: string
  repaid_invoice_number: string
  repaid_check_number?: string
  rebill_invoice_id: string
  rebill_invoice_number: string
  /** Decimal-serialized by the server, so it may arrive as a string. */
  amount: number | string
}

export interface CreditMemoPreviewLine {
  gl_account_id: string
  gl_account_name: string
  amount: number
  description?: string
  /** Per-deduction mode only: this line's own credit note execution date (YYYY-MM-DD) and invoice reference. */
  execution_date?: string
  /**
   * Xero per-deduction mode only: the date this line's credit note will actually post on, set when
   * `execution_date` falls inside a period locked in Xero. Absent means it posts on its own
   * execution date.
   */
  posting_date?: string
  reference?: string
  /** Accounting item (product/service) this line posts through, from the coded ledger row. */
  product_service?: string
  /** The line's product group, when the org's lines carry one (`product_group_field`). */
  product_group?: string
  /** The line's chargeback type, when the org's lines carry one (`chargeback_type_field`). */
  chargeback_type?: string
}

/**
 * A transaction on the check whose cash counts toward the check total but whose amount never
 * reaches the credit memo, because it carries no GL coding.
 */
export interface UncodedTransaction {
  deduction_id: string
  invoice_number: string
  kind: TransactionKind
  /** Dollars of this transaction no credit memo line covers — the uncovered remainder if partly coded. */
  missing_amount: number
}

/** A transaction on the check coded for *more* than the check carries for it. See the preview's `overcoded_transactions`. */
export interface OvercodedTransaction {
  deduction_id: string
  invoice_number: string
  kind: TransactionKind
  /** What the transaction can carry: its cash, or an early-pay invoice's discount. */
  codeable_amount: number | string
  /** The signed total of its splits, as a magnitude. */
  coded_amount: number | string
  /** `coded_amount - codeable_amount`, always positive. */
  excess_amount: number | string
}

/** How one UNFI `PP` (prepayment) line on the check was read. See the preview's `prepayments`. */
export type PrepaymentRole = "self_cancelling" | "advance" | "reversal" | "partial_reversal"

export interface ClassifiedPrepayment {
  deduction_id: string
  /** The `PP` row's own invoice number, e.g. `SI00003930PP`. */
  invoice_number: string
  /** The invoice it belongs to, e.g. `SI00003930`. */
  invoice_stem: string
  role: PrepaymentRole
  /** Magnitude of the advance, always positive. Serialized as a decimal string by the server. */
  amount: number | string
  /** Reversals only: the invoice line on this check the reversal cancels; absent means unresolved. */
  settled_invoice_id?: string
  /** That line's number as printed on the remittance (`INV33141-0-East`). */
  settled_invoice_number?: string
  /** Partial reversals only: the advance less the re-listed line's cash, recorded as a deduction. */
  clawback?: number | string
}

export interface RepaymentInvoiceLine {
  gl_account_id: string
  gl_account_name?: string | null
  /** Positive; Decimal-serialized, so it may arrive as a string. */
  amount: number | string
  reference?: string | null
  memo?: string | null
  /** The accounting-system item the line was coded to (QuickBooks product/service). */
  product_service?: string | null
}

export interface RepaymentInvoicePreview {
  /** The repayment references on the check, e.g. "SI00003705A". */
  reference: string
  /** Invoice total: the check's negative credit-memo lines, as a positive number. */
  total: number | string
  lines: RepaymentInvoiceLine[]
  /** What the credit notes total once the negative lines are on the invoice. */
  credit_total: number | string
  supported: boolean
}

export interface CashApplicationPreview {
  check_id: string
  accounting_check_id?: string
  check_number: string
  customer_name?: string
  customer_remote_id?: string
  check_date?: string
  check_amount: number
  invoice_assignments: InvoiceAssignmentLine[]
  unassigned_count: number
  credit_memo_lines: CreditMemoPreviewLine[]
  all_assigned: boolean
  existing_credit_memo_id?: string
  /** Display name of the connected accounting system (e.g. "QuickBooks", "Sage Intacct"). */
  accounting_system: string
  /**
   * Xero only: credit-memo GL account codes Xero won't accept as an `AccountCode` (code-less
   * accounts). Non-empty blocks applying until the accounts are given Codes in Xero. Omitted/empty
   * for other providers or when every code is valid.
   */
  gl_code_issues?: string[]
  /**
   * Xero only: the connection lacks `accounting.attachments`, so credit notes would be created
   * without their deductions' backup until the brand reconnects Xero. Never blocks applying.
   */
  xero_backup_attachments_blocked?: boolean
  /**
   * Transactions on the check contributing nothing to the credit memo because they are not
   * GL-coded. Non-empty always means the two apply-cash sides cannot balance, so this is reported
   * in place of the balance gap — the gap is the symptom, this is the cause.
   */
  uncoded_transactions?: UncodedTransaction[]
  /**
   * NetSuite with a product-group line field (1st Phorm's "Related Product Group"): what the brand
   * calls it. Each line then shows its group, and lines without one are flagged (never blocking).
   */
  product_group_field?: string
  /** NetSuite with a chargeback-type line field: what the brand calls it. */
  chargeback_type_field?: string
  /**
   * The mirror image: transactions coded for more than the check carries for them (a repayment
   * coded at its gross when the customer kept the terms discount). Also always unbalances the
   * sides, by dollars nobody remitted, and is reported in place of the balance gap.
   */
  overcoded_transactions?: OvercodedTransaction[]
  /**
   * UNFI prepayment (`PP`) lines on this check and how they were read. Non-empty means some of the
   * check's cash pays an invoice not otherwise listed on it (an advance, shown below as that
   * invoice's payment), or an invoice listed on it was already paid by an earlier check (a
   * reversal, excluded together with the line it settled). A reversal with no matching invoice
   * line blocks applying.
   */
  prepayments?: ClassifiedPrepayment[]
  /**
   * Set when the credit memo nets below zero: the customer is paying money back and no invoice on
   * the check absorbs it. Xero gets a sales invoice for the repayment (`supported`); other providers
   * cannot apply the check from here.
   */
  repayment_invoice?: RepaymentInvoicePreview
  /** Repayments on this check that pay a rebill invoice; each shows as that invoice's payment line. */
  rebill_settlements?: RebillSettlement[]
  /**
   * Xero only: the tenant's effective lock date (the later of its period and end-of-year locks).
   * Xero rejects any document dated on or before it and the API has no advisor override, so credit
   * notes dated inside it post in the earliest open period instead. Absent means no lock is set.
   */
  xero_lock_date?: string
  /**
   * Sage holding mode: the GL account number deductions on this check park in instead of (or in
   * addition to) their own coding. When set, uncoded transactions never block the apply — they are
   * cleared later from the Open Deductions screen.
   */
  open_deductions_account?: string
  open_deductions_account_name?: string
  /**
   * Which deductions park: "all_deductions" (review-first), "uncoded_only" (partial apply) or
   * "by_stage" (the stage decides: closed-out, fully coded deductions post directly; the rest parks
   * and syncs once its stage is closed out).
   */
  open_deductions_scope?: OpenDeductionsScope
  /**
   * The accounting integration whose settings hold the open-deductions account, when the provider
   * supports the holding flow (Sage Intacct, NetSuite). Present even before an account is chosen so
   * the apply page can offer the picker inline.
   */
  open_deductions_integration_id?: string
  /**
   * Sage multi-DC mode: this check's invoices span several DC-level Sage customers, so applying
   * creates one credit memo + one AR payment per customer listed here (Sage has no parent-customer
   * cash application). Empty/absent for single-customer checks.
   */
  dc_groups?: DcGroupPreview[]
  /** Multi-DC mode: why the per-customer plan can't be built; applying fails until resolved. */
  dc_plan_error?: string
  /**
   * The org's early-pay discount account (Settings → auto-code), when set and active: the default
   * account for booking an invoice's remaining gap as an additional discount.
   */
  early_pay_account_id?: string
  /**
   * Early-pay discounts ride on the payment ("Disc. Taken") rather than the credit memo. When true an
   * invoice's remaining gap is taken on the payment too, with no account or item needed.
   */
  early_pay_on_payment?: boolean
  /**
   * KeHE checks with a BI allowance account set (Settings → KeHE): each invoice's BI-sized short is
   * written off to this account on the credit memo (`plan_bi_allowance_rows`), and the apply is asked
   * to do the same (`book_bi_allowance`).
   */
  bi_allowance?: BiAllowancePreview | null
  /**
   * Deductions the accounting system has already applied to one of this check's invoices as a
   * credit note. Not on the credit memo and not in `invoice_assignments`; each named invoice is
   * applied for its total less what its note settled, which is the balance the system has due.
   */
  already_credited?: AlreadyCreditedLine[]
  /** Deductions folded into their invoice's payment line as price true-ups (`PriceTrueUp`). */
  price_true_ups?: PriceTrueUp[]
  /** Closed invoices paid and taken back on this check (`ClosedInvoiceWash`); off the check entirely. */
  closed_invoice_washes?: ClosedInvoiceWash[]
  /**
   * NetSuite only: the check spans several Ahold vendor accounts, which the brand may keep as
   * separate customers. Omitted/empty for every other check.
   */
  customer_groups?: CustomerGroupPreview[]
  /**
   * Whether an invoice line's cash can be left unapplied on the customer's account instead of being
   * matched to an invoice (`ApplyCashApplicationRequest.unapplied_deduction_ids`) — for a line paying
   * an invoice the accounting system already shows as paid in full. NetSuite only, and not on a
   * check applied customer-by-customer.
   */
  unapplied_cash_supported?: boolean
  /**
   * Per invoice on the check, what the apply will post — the deductions taken against it, its
   * early-pay discount, the credit the memo lands on it and the cash the payment carries — plus
   * the credit no invoice owns and where it drains. Built by the server from the apply's own
   * planners on the picks sent with the request; never recomputed here.
   */
  invoice_breakdown: InvoiceBreakdownPreview
}

/** One remittance row as the per-invoice breakdown lists it: attributed to an invoice, or pooled. */
export interface BreakdownRow {
  deduction_id: string
  /** The row's number as remitted (`3662299C1`). */
  invoice_number: string
  kind: DeductionKind
  /** Signed as remitted: negative for a deduction, positive for a repayment. */
  amount: number | string
}

/**
 * One invoice on the check as the apply will settle it. Every amount is positive and
 * `invoice_total - left_open = early_pay_discount + credit_applied + cash`.
 */
export interface InvoiceBreakdownLine {
  /** Absent for the sales invoice a net-negative memo needs, which the apply creates first. */
  accounting_invoice_id?: string
  invoice_number: string
  invoice_total: number | string
  /** The check's invoice lines matched to this invoice — the picks the breakdown was built from. */
  line_deduction_ids: string[]
  /** What those lines remitted for the invoice, before the deductions below come off. */
  remitted_cash: number | string
  /** The deductions taken against this invoice: linked to it, or numbered against its line. */
  deductions: BreakdownRow[]
  early_pay_discount: number | string
  /** Whether that discount rides on the payment ("Disc. Taken") or on the credit memo. */
  discount_on_payment: boolean
  /** What this check leaves due on the invoice (a short pay, another check's installment, a credit note already applied). */
  left_open: number | string
  /** Credit the invoice takes because the remittance took it against this invoice. */
  own_credit: number | string
  /** Credit it takes from the pool because it had room left and came first. */
  pooled_credit: number | string
  /** `own_credit + pooled_credit`: what the credit memo settles on the invoice. */
  credit_applied: number | string
  /** What the payment carries for the invoice. */
  cash: number | string
}

/** The credit no invoice on the check owns, and where it goes. */
export interface CreditPoolPreview {
  /** Rows that name no invoice on the check. */
  rows: BreakdownRow[]
  /** The credit left once every invoice has taken its own share. */
  total: number | string
  /** The invoices that take pooled credit, first to last. */
  drains_into: string[]
  /** Pooled credit no invoice had room for; non-zero means the apply will refuse. */
  unallocated: number | string
}

export interface InvoiceBreakdownPreview {
  invoices: InvoiceBreakdownLine[]
  pool: CreditPoolPreview
  /** The credit the memo brings to the allocation. */
  credit_total: number | string
  /** Σ `cash`: what the payment will carry. */
  cash_total: number | string
}

/**
 * One Ahold vendor account's side of a multi-customer check (NetSuite only), totalled the way the
 * remittance subtotals its "Legacy Vendor/Customer" groups.
 */
export interface CustomerGroupPreview {
  /** Normalized legacy vendor number; empty for rows that carry none. */
  key: string
  /** The customer saved for this vendor account on an earlier apply. */
  saved_customer_remote_id?: string | null
  /** The customer this vendor account's own invoices are billed to, when they all agree. */
  suggested_customer_remote_id?: string | null
  transaction_count: number
  invoice_cash: number | string
  deduction_total: number | string
  /** Negative means applying posts an A/R journal entry to cover it from another group's cash. */
  net: number | string
}

/** The accounting customer chosen for one `CustomerGroupPreview`. */
export interface CustomerGroupSelection {
  key: string
  customer_id: string
  customer_name?: string
}

/** One planned per-customer application shown on the apply page in Sage multi-DC mode. */
export interface DcGroupPreview {
  customer_id: string
  customer_name?: string | null
  invoice_count: number
  invoice_total: number
  /** Deduction credit attributed to this DC (invoice match, split customer, or UNFI DC name). */
  direct_credit: number
  /** This DC's proportional share of the deductions that couldn't be attributed to any DC. */
  spread_credit: number
  /** Cash applied to this customer's invoices (invoice total less the credit memo). */
  cash: number
}

/**
 * `invoiceAssignments` are the page's current picks, so the server builds `invoice_breakdown` on
 * what the apply will persist. Nothing is written; omit them on first load.
 */
export async function prepareCashApplication(
  checkId: string,
  accountingCheckId?: string,
  invoiceAssignments?: InvoiceAssignmentInput[]
): Promise<Result<HttpResponse<CashApplicationPreview>, HttpError>> {
  return await api_fetch<CashApplicationPreview>("/accounting/cash-application/prepare", {
    method: "POST",
    body: {
      check_id: checkId,
      accounting_check_id: accountingCheckId,
      invoice_assignments: invoiceAssignments ?? [],
    },
  })
}

export interface InvoiceAssignmentInput {
  deduction_id: string
  accounting_invoice_id: string
}

export interface ConfirmCashApplicationResponse {
  credit_memo_id: string
}

export async function confirmCashApplication(
  checkId: string,
  accountingCheckId: string,
  invoiceAssignments: InvoiceAssignmentInput[]
): Promise<Result<HttpResponse<ConfirmCashApplicationResponse>, HttpError>> {
  return await api_fetch<ConfirmCashApplicationResponse>("/accounting/cash-application/confirm", {
    method: "POST",
    body: {
      check_id: checkId,
      accounting_check_id: accountingCheckId,
      invoice_assignments: invoiceAssignments,
    },
  })
}

// Credit Memo types and functions

export interface CreditMemoLineData {
  gl_account_id: string
  gl_account_name?: string
  amount: number
  memo?: string
  location_id?: string
  /** Accounting item (product/service) this line posts through, from the coded ledger row. */
  product_service?: string
}

/** An active accounting item (product/service) a ledger account can be mapped to. */
export interface AccountingItemOption {
  id: string
  name: string
  item_type?: string | null
}

/** GET /accounting/items response — `error` set when the live query failed (picker falls back to free text). */
export interface AccountingItemsResponse {
  items: AccountingItemOption[]
  error?: string | null
}

/**
 * The connected accounting system's active items usable on credit memo lines, queried live.
 * Empty for non-item-based providers (Sage/Xero) or when nothing is connected.
 */
export async function listAccountingItems(): Promise<Result<HttpResponse<AccountingItemsResponse>, HttpError>> {
  // Cached: the live item query can take seconds, and every ledger row's add-item dialog uses it.
  return await api_fetch<AccountingItemsResponse>("/accounting/items", { use_cache: true })
}

export interface CreditMemoData {
  customer_id: string
  customer_name?: string
  reference_number?: string
  description?: string
  date_created: string
  lines: CreditMemoLineData[]
  remote_key?: string
  remote_record_no?: string
  /**
   * Ledger account the apply booked additional-discount lines to (one per invoice the check settled
   * short of its total). Carried back into a retry so the same lines are rebuilt.
   */
  additional_discount_account_id?: string
  /** Early pay on the payment: the apply added each invoice's gap to its "Disc. Taken" instead. */
  additional_discount_on_payment?: boolean
  /** The apply wrote KeHE's BI allowance off each short invoice; a retry books the same lines. */
  book_bi_allowance?: boolean
}

export interface CreditMemoInvoiceAssignment {
  deduction_id: string
  invoice_number: string
  deduction_amount: number
  accounting_invoice_id?: string
  accounting_invoice_number?: string
}

export interface CreditMemoDetail {
  id: string
  org_id: string
  check_id: string
  check_number: string
  created_at: string
  published_at?: string
  publish_error?: string
  /**
   * Whether the memo's check has been applied (payment created, or an external credit memo detected
   * by sync). False after a publish-only retry: the memo exists in the accounting system but
   * nothing has applied it to the invoices yet.
   */
  check_applied: boolean
  data: CreditMemoData
  invoice_assignments: CreditMemoInvoiceAssignment[]
}

export async function getCreditMemo(
  id: string
): Promise<Result<HttpResponse<CreditMemoDetail>, HttpError>> {
  return await api_fetch<CreditMemoDetail>(`/credit-memo/${id}`)
}

export async function updateCreditMemo(
  id: string,
  payload: {
    description?: string
    reference_number?: string
    lines?: CreditMemoLineData[]
    invoice_assignments?: InvoiceAssignmentInput[]
  }
): Promise<Result<HttpResponse<CreditMemoDetail>, HttpError>> {
  return await api_fetch<CreditMemoDetail>(`/credit-memo/${id}`, {
    method: "PUT",
    body: payload,
  })
}

export interface PublishCreditMemoResponse {
  id: string
  published_at?: string
  publish_error?: string
  remote_key?: string
  remote_record_no?: string
  /** QuickBooks only: Product/Service items missing for the coded GL accounts. Retry with create_missing_items to create them. */
  missing_product_service_items?: string[]
}

export async function publishCreditMemo(
  id: string,
  options?: { create_missing_items?: boolean }
): Promise<Result<HttpResponse<PublishCreditMemoResponse>, HttpError>> {
  return await api_fetch<PublishCreditMemoResponse>(`/credit-memo/${id}/publish`, {
    method: "POST",
    body: options ?? {},
  })
}

// Apply cash application (create credit memo + payment in QuickBooks in one action)

export interface ApplyCashApplicationRequest {
  check_id: string
  customer_id: string
  customer_name?: string
  deposit_account_remote_id?: string
  /** Booking date for the payment (ISO yyyy-MM-dd). Defaults to the check date. */
  payment_date?: string
  invoice_assignments: InvoiceAssignmentInput[]
  /** QuickBooks only: create missing Product/Service items before publishing (explicit user confirmation). */
  create_missing_items?: boolean
  /**
   * Xero only: apply even though the selected invoices exceed cash + credit memo, settling each
   * shorted invoice for total − short (remainder stays due in Xero) and recording short_pay rows.
   * Explicit opt-in from the apply-anyway confirmation.
   */
  allow_short_pay?: boolean
  /**
   * The other treatment of the same gap, for orgs with an open-deductions holding account: book each
   * shorted invoice's unpaid balance as a deduction on the check, so every invoice closes in full and
   * the short parks in the holding account and shows in the deduction list to dispute.
   */
  short_pay_as_deduction?: boolean
  /**
   * Xero per-deduction mode only: post credit-note lines originally dated `execution_date` on
   * `posting_date` instead. Keyed by execution date because moving a period is one decision for
   * every line in it. Omitting it is safe — the server still moves anything Xero's lock would
   * reject onto the payment date. Our stored execution dates are never rewritten either way.
   */
  posting_dates?: PostingDateOverride[]
  /**
   * Book each selected invoice's remaining gap (its total less what the check settles for it) as an
   * additional-discount credit memo line to this ledger account, one line per invoice. Walmart takes
   * its early pay off the pre-allowance subtotal, so its remittance discount under-states what it kept
   * by a few dollars per invoice. Explicit opt-in; the server recomputes the gaps and refuses any wider
   * than 5% of the invoice.
   */
  additional_discount_account_id?: string
  /**
   * Orgs that take early pay on the payment: add each invoice's gap to its "Disc. Taken" instead of
   * booking credit memo lines. Same 5% cap, recomputed server-side.
   */
  additional_discount_on_payment?: boolean
  /** Write KeHE's BI allowance off each short invoice to the configured account (recomputed server-side). */
  book_bi_allowance?: boolean
  /**
   * NetSuite only: post one credit memo + one payment per customer, one entry per
   * `CustomerGroupPreview.key`. Groups given the same customer merge; a side that nets negative is
   * covered by an A/R journal entry. When set, `customer_id` is ignored, and the choices are saved
   * for the next check.
   */
  customer_groups?: CustomerGroupSelection[]
  /**
   * NetSuite only: invoice lines whose cash is left unapplied on the customer's account instead of
   * being matched to an invoice. The payment is still created for the whole check; NetSuite keeps
   * what the matched invoices do not need as the payment's unapplied balance. A line listed here
   * must not also be in `invoice_assignments`. The server recomputes the amount.
   */
  unapplied_deduction_ids?: string[]
}

/** One user-chosen posting date for every credit-note line originally dated `execution_date`. */
export interface PostingDateOverride {
  /** The line's own execution date, ISO yyyy-MM-dd, exactly as the preview reported it. */
  execution_date: string
  posting_date: string
}

export interface ApplyCashApplicationResponse {
  check_id: string
  credit_memo_id: string
  credit_memo_published: boolean
  credit_memo_remote_id?: string
  payment_created: boolean
  payment_remote_id?: string
  error?: string
  /** Cash applied, but something is left to finish by hand in the accounting system. */
  warning?: string
  /** QuickBooks only: Product/Service items missing for the coded GL accounts. Retry with create_missing_items to create them. */
  missing_product_service_items?: string[]
}

export async function applyCashApplication(
  request: ApplyCashApplicationRequest
): Promise<Result<HttpResponse<ApplyCashApplicationResponse>, HttpError>> {
  return await api_fetch<ApplyCashApplicationResponse>("/accounting/cash-application/apply", {
    method: "POST",
    body: request,
  })
}

// Short pays: invoices settled for less than their accounting-system total — recorded at apply
// time, or found by comparing every synced invoice against what our distributor/retailer data says
// was paid for it.

export type ShortPayStatus = "open" | "resolved" | "ignored"

export interface ShortPayJson {
  id: string
  amount: number
  status: ShortPayStatus
  resolved_note?: string
  created_at: string
  accounting_invoice_id: string
  deduction_id: string
  check_id?: string
  /** Set once a dispute has been filed asking for this money back. */
  dispute_id?: string
  invoice_number: string
  invoice_total: number
  check_number?: string
  check_date?: string
  customer_name: string
}

/** How a detected short pay's paying transaction was tied to the accounting invoice. */
export type ShortPayMatchedBy = "link" | "invoice_number"

/** An underpayment found by the proactive comparison, not yet recorded as a short pay. */
export interface DetectedShortPayJson {
  accounting_invoice_id: string
  deduction_id: string
  invoice_number: string
  invoice_date?: string
  invoice_total: number
  paid: number
  discount: number
  allowance: number
  amount: number
  /** Balance the accounting system still shows outstanding; absent when it doesn't track one. */
  still_due?: number
  /** The collectable slice of `amount` — capped at `still_due`. The list ranks on this. */
  outstanding: number
  check_number?: string
  check_date?: string
  customer_name: string
  matched_by: ShortPayMatchedBy
  payment_count: number
}

/** A customer shorting a consistent percentage across several invoices — a deal term, not a one-off. */
export interface ShortPayPatternJson {
  customer_name: string
  percent: number
  invoice_count: number
  amount: number
  outstanding: number
}

export interface ShortPayListResponse {
  eligible: boolean
  accounting_system?: string
  /** Whether `/accounting/invoice/{id}/pdf` can render invoices for this org's provider (Xero, QuickBooks). */
  invoice_pdf_available: boolean
  synced_invoice_count: number
  recorded: ShortPayJson[]
  detected: DetectedShortPayJson[]
  patterns: ShortPayPatternJson[]
  min_amount: number
  months: number
}

export interface ShortPaySummaryJson {
  eligible: boolean
  open_count: number
}

export async function listShortPays(): Promise<Result<HttpResponse<ShortPayListResponse>, HttpError>> {
  return await api_fetch<ShortPayListResponse>("/accounting/short-pays")
}

export async function fetchShortPaySummary(): Promise<ShortPaySummaryJson> {
  return await api_fetch_throws<ShortPaySummaryJson>("/accounting/short-pays/summary")
}

/** The saved `short_pay` row, which carries none of the invoice/check context the list joins in. */
export interface RecordedShortPayRef {
  id: string
  created_at: string
}

/** Record a detected short pay (with the status the user chose) so it can be tracked like an apply-time one. */
export async function recordShortPay(
  accounting_invoice_id: string,
  deduction_id: string,
  status: ShortPayStatus,
  resolved_note?: string
): Promise<Result<HttpResponse<RecordedShortPayRef>, HttpError>> {
  return await api_fetch<RecordedShortPayRef>("/accounting/short-pays", {
    method: "POST",
    body: { accounting_invoice_id, deduction_id, status, resolved_note },
  })
}

/** A file we already hold and will attach to the dispute without the user doing anything. */
export interface ShortPayDisputeAttachmentJson {
  name: string
  s3_uri: string
}

export interface ShortPayDisputeFiledJson {
  dispute_id: string
  sent_at?: string
}

/** The email we would send the distributor for a short pay, and what the user still has to add. */
export interface ShortPayDisputePreview {
  can_file: boolean
  blocked_reason?: string
  to: string
  subject: string
  body: string
  /** The unpaid balance the draft asks for (decimal string), "0" when the server could not derive it. */
  amount: string
  /** Where `amount` came from; `unknown` means the user has to enter it before filing. */
  amount_source: "requested" | "short_pay" | "accounting_invoice" | "unknown"
  instructions: string[]
  attachments: ShortPayDisputeAttachmentJson[]
  /** Files generated and attached automatically on send — UNFI's pay inquiry form, filled in. */
  generated_attachments: string[]
  already_filed?: ShortPayDisputeFiledJson
}

export async function fetchShortPayDisputePreview(id: string): Promise<Result<HttpResponse<ShortPayDisputePreview>, HttpError>> {
  return await api_fetch<ShortPayDisputePreview>(`/accounting/short-pay/${id}/dispute`)
}

/**
 * The same preview keyed off the invoice payment row (a `kind = invoice` deduction), for brands
 * without an accounting integration. `amount` is what the user entered when we could not derive it.
 */
export async function fetchDeductionShortPayDisputePreview(
  deduction_id: string,
  amount?: number | null
): Promise<Result<HttpResponse<ShortPayDisputePreview>, HttpError>> {
  const query = amount && amount > 0 ? `?amount=${encodeURIComponent(amount.toFixed(2))}` : ""
  return await api_fetch<ShortPayDisputePreview>(`/deduction/${deduction_id}/short-pay-dispute${query}`)
}

/**
 * File the short pay with the distributor. Multipart because the user attaches the invoice here —
 * it lives in their accounting system and no connector we have returns its PDF.
 *
 * Raw `fetch` rather than `api_fetch`, which JSON-stringifies its body and pins
 * `Content-Type: application/json`; multipart needs the browser to set the boundary itself. Same
 * reason `submit_dispute` does it this way.
 */
export async function fileShortPayDispute(
  id: string,
  message: string,
  files: File[],
  existing_files: string[]
): Promise<Result<unknown, Error>> {
  return await post_short_pay_dispute_form(`/api/accounting/short-pay/${id}/dispute`, message, files, existing_files, null)
}

/** File the short pay on an invoice payment row; `amount` is required when the preview could not derive it. */
export async function fileDeductionShortPayDispute(
  deduction_id: string,
  amount: number | null,
  message: string,
  files: File[],
  existing_files: string[]
): Promise<Result<unknown, Error>> {
  return await post_short_pay_dispute_form(`/api/deduction/${deduction_id}/short-pay-dispute`, message, files, existing_files, amount)
}

async function post_short_pay_dispute_form(
  url: string,
  message: string,
  files: File[],
  existing_files: string[],
  amount: number | null
): Promise<Result<unknown, Error>> {
  let form = new FormData()
  form.append("message", message)
  form.append("existing_files", JSON.stringify(existing_files))
  if (amount != null && amount > 0) {
    form.append("amount", amount.toFixed(2))
  }
  for (let file of files) {
    form.append("files", file)
  }
  let res = await fetch(url, {
    method: "POST",
    credentials: "same-origin",
    body: form,
  })
  let body = await res.json().catch(() => ({}))
  if (!res.ok) {
    return Err(new Error(body.message ?? "Failed to file the short pay dispute."))
  }
  return Ok(body)
}

export async function updateShortPay(
  id: string,
  status: ShortPayStatus,
  resolved_note?: string
): Promise<Result<HttpResponse<unknown>, HttpError>> {
  return await api_fetch<unknown>(`/accounting/short-pay/${id}`, {
    method: "POST",
    body: { status, resolved_note },
  })
}

// Month-end journal entry (NetSuite): posts a check's deduction GL detail when its cash was
// already applied externally (an accounting-system credit memo exists for the check).

export interface CheckJournalEntryJson {
  journal_entry_id: string
  check_id: string
  published_at?: string
  remote_key?: string
  error?: string
}

export async function getCheckJournalEntry(
  check_id: string
): Promise<Result<HttpResponse<CheckJournalEntryJson | null>, HttpError>> {
  return await api_fetch<CheckJournalEntryJson | null>(`/accounting/journal-entry?check_id=${check_id}`)
}

export interface PostCheckJournalEntryRequest {
  check_id: string
  /** Posting date (ISO yyyy-MM-dd). Defaults to today so it lands in the open period. */
  posting_date?: string
}

export async function postCheckJournalEntry(
  request: PostCheckJournalEntryRequest
): Promise<Result<HttpResponse<CheckJournalEntryJson>, HttpError>> {
  return await api_fetch<CheckJournalEntryJson>("/accounting/journal-entry", {
    method: "POST",
    body: request,
  })
}

// Open deductions (holding account — Sage Intacct or NetSuite)

export type OpenDeductionsScope = "all_deductions" | "uncoded_only" | "by_stage"

export interface OpenDeductionSplitJson {
  /** The coded ledger account — what a rebill of this deduction credits by default. */
  account_id?: string | null
  account_number?: string | null
  expense_account?: string | null
  amount: number
}

export interface OpenDeductionRow {
  entry_id: string
  deduction_id: string
  invoice_number: string
  check_number?: string | null
  customer_name: string
  description?: string | null
  kind: string
  /** What still sits in the holding account (what parked less what partial syncs already moved). */
  amount: number
  /** What partial syncs already moved out of the holding account while the row stayed open. */
  cleared_amount?: number
  /** The deduction's own amount and early-pay discount — what bulk coding books (`coding_amount`). */
  invoice_amount: number
  discount_amount?: number | null
  /** GL date the amount entered the holding account. */
  posted_date: string
  /** When the deduction was executed in-store — the origin period for retroactive clearing. */
  execution_date?: string | null
  category_name?: string | null
  category_description?: string | null
  /** The deduction's current stage (`new`, `validated`, `expensable`, …). */
  stage: string
  /** Under `by_stage`, whether the stage alone permits syncing (coding is checked separately). */
  stage_ready: boolean
  validated: boolean
  /** Fully coded, coding matches the held amount and (under `by_stage`) the stage is closed out — clearable. */
  ready: boolean
  blocked_reason?: string
  /** Only part of what is held is coded: that part can sync on its own and the rest stays parked. */
  partial?: PartialClear | null
  splits: OpenDeductionSplitJson[]
  cleared_at?: string | null
  /** A clear batch drafted a journal entry for this row but it hasn't reached the accounting system yet. */
  clearing_pending: boolean
  /** Why the accounting system refused this row's last sync. The row is still open and can be synced again. */
  sync_error?: string | null
  /** The invoice this deduction was rebilled on, if it has been. */
  rebill?: OpenDeductionRebill | null
}

/** What syncing a partly coded row does: `moving` leaves the holding account, `remaining` stays. */
export interface PartialClear {
  moving: number
  remaining: number
}

/** A deduction billed back to the customer: the invoice its repayment is applied to. */
export interface OpenDeductionRebill {
  invoice_remote_id: string
  invoice_number?: string | null
  amount: number
  invoice_date: string
  created_at: string
}

export interface OpenDeductionsSummary {
  open_count: number
  open_total: number
  ready_count: number
  ready_total: number
  uncoded_count: number
  uncoded_total: number
  cleared_count: number
  cleared_total: number
}

export interface OpenDeductionsResponse {
  holding_account?: string | null
  holding_account_name?: string | null
  /** Sage only: the journal reclassification entries post to. */
  gl_journal_id?: string | null
  /** "NetSuite" / "Sage Intacct" — the system the holding account lives in. */
  accounting_system?: string
  scope?: OpenDeductionsScope
  summary: OpenDeductionsSummary
  rows: OpenDeductionRow[]
}

export async function listOpenDeductions(): Promise<Result<HttpResponse<OpenDeductionsResponse>, HttpError>> {
  return await api_fetch<OpenDeductionsResponse>("/accounting/open-deductions")
}

export interface ClearOpenDeductionsRequest {
  entry_ids: string[]
  /** GL posting date (ISO yyyy-MM-dd); pick a date inside the origin period to clear retroactively. */
  posting_date: string
  memo?: string
  /** Sync the coded part of partly coded rows and leave the rest parked. */
  allow_partial?: boolean
}

export interface ClearPreviewLine {
  gl_account_id: string
  gl_account_name?: string | null
  amount: number
  memo?: string | null
  /** The Sage customer dimension stamped on this leg (split/DC attribution), when resolved. */
  customer_name?: string | null
}

export interface ClearBlockedEntry {
  entry_id: string
  invoice_number: string
  reason: string
}

/** A selected row the batch syncs only in part. */
export interface ClearPartialEntry {
  entry_id: string
  invoice_number: string
  moving: number
  remaining: number
}

export interface ClearPreviewResponse {
  lines: ClearPreviewLine[]
  holding_account: string
  holding_account_name?: string | null
  holding_total: number
  entry_count: number
  posting_date: string
  blocked: ClearBlockedEntry[]
  /** Rows this batch syncs only in part: what moves and what stays parked. */
  partial?: ClearPartialEntry[]
  accounting_system: string
}

export async function previewOpenDeductionClear(
  request: ClearOpenDeductionsRequest
): Promise<Result<HttpResponse<ClearPreviewResponse>, HttpError>> {
  return await api_fetch<ClearPreviewResponse>("/accounting/open-deductions/preview", { method: "POST", body: request })
}

export interface ClearOpenDeductionsResponse {
  /** Null when the batch was an even wash (legs net to zero) and no journal entry was needed. */
  journal_entry_id?: string | null
  published: boolean
  remote_key?: string | null
  cleared_count: number
  cleared_total: number
  /** How many of those rows synced only in part and are still open for the rest. */
  partial_count?: number
  error?: string
}

export async function clearOpenDeductions(
  request: ClearOpenDeductionsRequest
): Promise<Result<HttpResponse<ClearOpenDeductionsResponse>, HttpError>> {
  return await api_fetch<ClearOpenDeductionsResponse>("/accounting/open-deductions/clear", { method: "POST", body: request })
}

export interface RebillOpenDeductionRequest {
  entry_id: string
  /** The GL account the rebill invoice credits. */
  ledger_account_id: string
  /** Invoice date (ISO yyyy-MM-dd). */
  invoice_date: string
}

/** Create the invoice a customer's repayment of this deduction will be applied to. */
export async function rebillOpenDeduction(
  request: RebillOpenDeductionRequest
): Promise<Result<HttpResponse<{ rebill: OpenDeductionRebill }>, HttpError>> {
  return await api_fetch<{ rebill: OpenDeductionRebill }>("/accounting/open-deductions/rebill", { method: "POST", body: request })
}

// QuickBooks customers + default deposit account

export interface QbCustomer {
  remote_id: string
  display_name: string
}

export async function listQbCustomers(): Promise<Result<HttpResponse<QbCustomer[]>, HttpError>> {
  return await api_fetch<QbCustomer[]>("/accounting/qb-customers")
}

/** A ledger account / GL account (reason_code row) usable as a deposit account. */
export interface LedgerAccountJson {
  id: string
  account_number: string
  expense_account: string
  remote_id: string
  account_type?: string
  /** Provider item (product/service) name credit-memo lines for this account post through (QuickBooks/NetSuite). */
  product_service?: string | null
  is_default_for_payments: boolean
  hidden_from_deduction_matching: boolean
}

export async function listLedgerAccounts(
  includeHidden = false
): Promise<Result<HttpResponse<LedgerAccountJson[]>, HttpError>> {
  return await api_fetch<LedgerAccountJson[]>("/reason_codes", {
    params: includeHidden ? { include_hidden: "true" } : {},
  })
}

export async function setDefaultPaymentAccount(
  accountId: string
): Promise<Result<HttpResponse<void>, HttpError>> {
  return await api_fetch<void>("/accounting/ledger-account/default-for-payments", {
    method: "POST",
    body: { account_id: accountId },
  })
}

export type AutoCodeSource = "category_rule" | "early_pay" | "repayment_mirror"

export interface AutoCodePreviewSplit {
  account_id?: string
  account_number?: string
  account_name?: string
  /** Decimal, serialized as a string. */
  amount: string
}

export interface AutoCodePreviewRow {
  deduction_id: string
  invoice_number: string
  check_number?: string
  kind: DeductionKind
  invoice_amount: number
  category_name?: string
  source: AutoCodeSource
  splits: AutoCodePreviewSplit[]
}

export interface AutoCodePreviewResponse {
  rows: AutoCodePreviewRow[]
  /** Uncoded deductions in scope, coded or not. */
  uncoded_count: number
  skipped_zero_amount: number
  skipped_prepayments: number
}

export interface AutoCodeResponse {
  coded_count: number
  skipped_zero_amount: number
  skipped_prepayments: number
  enriched_advances: number
}

/** What auto-code would write. `deduction_ids` limits it to those rows; omit it for every uncoded deduction. */
export async function previewAutoCode(deduction_ids?: string[]): Promise<AutoCodePreviewResponse> {
  return await api_fetch_throws<AutoCodePreviewResponse>("/auto-code-deductions/preview", {
    method: "POST",
    body: { deduction_ids: deduction_ids ?? null },
  })
}

/** Run auto-code over `deduction_ids` (still-uncoded rows only). */
export async function applyAutoCode(deduction_ids: string[]): Promise<AutoCodeResponse> {
  return await api_fetch_throws<AutoCodeResponse>("/auto-code-deductions/apply", {
    method: "POST",
    body: { deduction_ids },
  })
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("is_check_number_conflict", () => {
    it("matches the server's existing-number client error", () => {
      expect(is_check_number_conflict(new HttpError(CHECK_NUMBER_EXISTS_MESSAGE, 400))).toBe(true)
    })

    it("ignores other errors and non-errors", () => {
      expect(is_check_number_conflict(new HttpError("There was a client error.", 400))).toBe(false)
      expect(is_check_number_conflict(new Error("Failed to update check"))).toBe(false)
      expect(is_check_number_conflict("A check with this number already exists")).toBe(false)
      expect(is_check_number_conflict(undefined)).toBe(false)
    })
  })
}
